import { ConfigurationError } from './ConfigurationError';
import { ENV_KEYS } from './EnvConstants';

export class EnvService {
  private static instance: EnvService;
  private environmentVariables: Map<string, string>;

  private constructor() {
    this.environmentVariables = new Map<string, string>();
    this.loadEnvironmentVariables();
  }

  public static getInstance(): EnvService {
    if (!EnvService.instance) {
      EnvService.instance = new EnvService();
    }
    return EnvService.instance;
  }

  private loadEnvironmentVariables(): void {
    const result = {
        parsed: {
            [ENV_KEYS.GEMINI_API_URL]: process.env[ENV_KEYS.GEMINI_API_URL],
            [ENV_KEYS.GEMINI_API_KEY]: process.env[ENV_KEYS.GEMINI_API_KEY],
            [ENV_KEYS.DENO_API_URL]: process.env[ENV_KEYS.DENO_API_URL],
            [ENV_KEYS.WEB_APP_URL]: process.env[ENV_KEYS.WEB_APP_URL],
        },
        error: null,
    }
    if (result.error) {
      throw new ConfigurationError('Failed to load environment variables');
    }
    for (const [key, value] of Object.entries(result.parsed || {})) {
      this.environmentVariables.set(key, value!);
    }
  }

  public get(key: string): string | undefined {
    return this.environmentVariables.get(key);
  }

  public getOrThrow(key: string): string {
    const value = this.get(key);
    if (value === undefined) {
      throw new Error(`Environment variable ${key} is not defined`);
    }
    return value;
  }

  public has(key: string): boolean {
    return this.environmentVariables.has(key);
  }

  public assertHas(...keys: string[]): void {
    const missing = keys.filter(key => !this.has(key));
    if (missing.length > 0) {
      throw new ConfigurationError(`Required environment variables are missing: ${missing.join(", ")}`);
    }
  }

  public getAll(): Record<string, string> {
    return Object.fromEntries(this.environmentVariables);
  }

  public getAsStringArray(key: string, separator = ","): string[] {
    const value = this.get(key);
    if (!value) {
      return [];
    }
    return value.split(separator).map(item => item.trim()).filter(Boolean);
  }
}

// Export a default instance
export const envService = EnvService.getInstance();